import { memo } from 'react';
import { Flex, Text } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { BorderedBox } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/BorderedBox.tsx';
import { DocumentTypeBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/DocumentTypeBadge.tsx';
import { useKYCDocumentsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/KYCDocuments/useKYCDocumentsController.ts';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { FormsTemplatesList } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/FormsTemplatesList.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import type {
	KycCertificationFlag,
	KycCertificationRequirementFlags,
	KycProofFlag,
	KycProofRequirementFlags,
} from '@dealstack/shared/lib/common/business/kycCertification';
import type { RequirementFlag } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/useKYCRequirementsController.ts';
import { ProofRequirementToggle } from './ProofRequirementToggle.tsx';

interface Props {
	proofs: KycProofRequirementFlags;
	certification: KycCertificationRequirementFlags;
	onProofChange: (flag: KycProofFlag, value: boolean) => void;
	onCertificationChange: (flag: KycCertificationFlag, value: boolean) => void;
	isSaving: (flag: RequirementFlag) => boolean;
}

const KYCDocuments = ({
	proofs,
	certification,
	onProofChange,
	onCertificationChange,
	isSaving,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { kycForm } = useKYCDocumentsController();

	return (
		<div>
			<Text size="B5Bold" mb="0.5rem">
				{tMipCreate('kyc.documents.title')}
			</Text>
			<FormProvider {...kycForm}>
				<Text size="B6Medium" c="dark.0" mb="0.5rem">
					{tMipCreate('kyc.documents.subtitle')}
				</Text>
				<BorderedBox
					header={
						<Flex direction="column" gap="0.5rem">
							<Text size="B6Bold">
								{tMipCreate('kyc.documents.individual_investors')}
							</Text>
							<Text size="B6Medium">
								{tMipCreate('kyc.documents.will_be_required_to_provide')}
							</Text>
							<Flex gap="0.5rem">
								<DocumentTypeBadge
									text={tMipCreate('kyc.documents.proof_identity')}
								/>
								<DocumentTypeBadge
									text={tMipCreate('kyc.documents.proof_address')}
								/>
							</Flex>
							<ProofRequirementToggle
								flag="isProofOfIdentityRequired"
								checked={proofs.isProofOfIdentityRequired}
								certificationChecked={
									certification.isProofOfIdentityCertificationRequired
								}
								isSaving={isSaving}
								onProofChange={onProofChange}
								onCertificationChange={(value) =>
									onCertificationChange(
										'isProofOfIdentityCertificationRequired',
										value
									)
								}
								certificationDataTestId={
									TEST_ELEMENT_IDS.MIP_CREATE_KYC_CERTIFY_IDENTITY_SWITCH
								}
							/>
							<ProofRequirementToggle
								flag="isProofOfAddressRequired"
								checked={proofs.isProofOfAddressRequired}
								certificationChecked={
									certification.isProofOfAddressCertificationRequired
								}
								isSaving={isSaving}
								onProofChange={onProofChange}
								onCertificationChange={(value) =>
									onCertificationChange(
										'isProofOfAddressCertificationRequired',
										value
									)
								}
								certificationDataTestId={
									TEST_ELEMENT_IDS.MIP_CREATE_KYC_CERTIFY_ADDRESS_SWITCH
								}
							/>
						</Flex>
					}
				>
					<Flex direction="column" gap="0.5rem">
						<Text size="B6Medium">{tMipCreate('kyc.documents.upload_or_select')}</Text>
						<FormsTemplatesList
							type={CustomDetailEnum.INDIVIDUAL}
							fileInputDataTestId={
								TEST_ELEMENT_IDS.MIP_CREATE_KYC_INDIVIDUAL_FILE_INPUT
							}
						/>
					</Flex>
				</BorderedBox>

				<BorderedBox
					header={
						<Flex direction="column" gap="0.5rem">
							<Flex gap="0.5rem">
								<Text size="B6Bold">
									<Text size="B6Medium">
										{tMipCreate('kyc.documents.personal_investment_vehicle')}
									</Text>
								</Text>
								<Text size="B6Medium" c="dark.0">
									{tMipCreate('kyc.documents.if_applicable')}
								</Text>
							</Flex>

							<Text size="B6Medium">
								{tMipCreate('kyc.documents.will_be_required_to_provide')}
							</Text>
							<Flex gap="0.5rem">
								<DocumentTypeBadge
									text={tMipCreate('kyc.documents.copy_of_share_register')}
								/>
								<DocumentTypeBadge
									text={tMipCreate(
										'kyc.documents.copy_of_certificate_of_registration'
									)}
								/>
								<DocumentTypeBadge
									text={tMipCreate(
										'kyc.documents.copy_of_articles_of_association'
									)}
								/>
							</Flex>
							<ProofRequirementToggle
								flag="isProofOfInvestmentVehicleRequired"
								checked={proofs.isProofOfInvestmentVehicleRequired}
								certificationChecked={
									certification.isProofOfInvestmentVehicleCertificationRequired
								}
								isSaving={isSaving}
								onProofChange={onProofChange}
								onCertificationChange={(value) =>
									onCertificationChange(
										'isProofOfInvestmentVehicleCertificationRequired',
										value
									)
								}
								certificationDataTestId={
									TEST_ELEMENT_IDS.MIP_CREATE_KYC_CERTIFY_VEHICLE_SWITCH
								}
							/>
						</Flex>
					}
				>
					<Flex direction="column" gap="0.5rem">
						<Text size="B6Medium">
							<Text size="B6Medium">
								{tMipCreate('kyc.documents.upload_or_select')}
							</Text>
						</Text>
						<FormsTemplatesList
							type={CustomDetailEnum.VEHICLE}
							fileInputDataTestId={TEST_ELEMENT_IDS.MIP_CREATE_KYC_PIV_FILE_INPUT}
						/>
					</Flex>
				</BorderedBox>
			</FormProvider>
		</div>
	);
};
const _KYCDocumentsExport = memo(KYCDocuments);

export { _KYCDocumentsExport as KYCDocuments };
